import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Upload, Download, FileSpreadsheet, X, Loader2, AlertCircle, Check, Send, ChevronRight, ChevronLeft, Trash2, Star } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_BODY = `Hej {first_name},

Vi tog fram ett snabbt utkast på en ny hemsida som vi tror skulle passa {company} riktigt bra. Du hittar förhandsvisningen här:

{preview_url}

På bokningssidan ser du även förslag på paket och kan boka in en kort genomgång om det är intressant.

— wedowedo`;

const DEFAULT_SMS = `Hej {first_name}! Vi gjorde en hemsida-preview för {company}: {preview_url} — vill du att vi tar 15 min och visar?`;

const DEFAULT_SUBJECT = "Hemsida för {company} — wedowedo";

export default function BulkOutreachModal({ onClose, onCreated }) {
  const [step, setStep] = useState(1); // 1: upload, 2: configure, 3: review, 4: done
  const [parsing, setParsing] = useState(false);
  const [rows, setRows] = useState([]);
  const [parseError, setParseError] = useState("");
  const [refs, setRefs] = useState([]);
  const [selectedRefs, setSelectedRefs] = useState([]);
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null);
  const [config, setConfig] = useState({
    subject_template: DEFAULT_SUBJECT,
    body_template: DEFAULT_BODY,
    sms_template: DEFAULT_SMS,
    intro_text: "",
    calendly_url: "",
    video_url: "",
    price_setup: 5000,
    price_monthly: 390,
    currency: "SEK",
    include_offer: true,
    photo_package_offer: false,
    admin_panel_offer: false,
    crm_offer: false,
  });

  const fetchRefs = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/reference-bank`, { withCredentials: true });
      setRefs(r.data || []);
    } catch {}
  }, []);
  useEffect(() => { fetchRefs(); }, [fetchRefs]);

  const downloadTemplate = async () => {
    try {
      const r = await axios.get(`${API}/admin/outreach/bulk-template`, { withCredentials: true, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = url; a.download = "outreach-bulk-template.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (err) { alert("Could not download template"); }
  };

  const onFile = async (file) => {
    if (!file) return;
    setParsing(true); setParseError(""); setRows([]);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/admin/outreach/bulk-parse`, fd, { withCredentials: true });
      setRows(r.data.rows || []);
      if ((r.data.rows || []).length === 0) setParseError("File appears empty.");
      else setStep(2);
    } catch (err) {
      setParseError(err.response?.data?.detail || "Could not parse file");
    }
    setParsing(false);
  };

  const validRows = rows.filter(r => r.valid);

  const createDrafts = async () => {
    setCreating(true);
    try {
      const refsSelected = refs.filter(r => selectedRefs.includes(r.id));
      const r = await axios.post(`${API}/admin/outreach/bulk-create-drafts`, {
        prospects: validRows,
        config: { ...config, references: refsSelected },
        batch_id: `bulk-${Date.now()}`,
      }, { withCredentials: true });
      setResult(r.data);
      setStep(4);
      if (onCreated) onCreated();
    } catch (err) {
      alert(err.response?.data?.detail || "Could not create drafts");
    }
    setCreating(false);
  };

  const removeRow = (rowIndex) => setRows(prev => prev.filter(r => r.row_index !== rowIndex));

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="bulk-outreach-modal">
      <div className="bg-[#0F1F3A] border border-[#1E3050] rounded-sm w-full max-w-5xl max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#1E3050]">
          <div>
            <h2 className="font-outfit text-xl text-white">Bulk Outreach</h2>
            <p className="text-xs text-[#857973] font-mono tracking-wider">STEP {step} OF 4 · {step === 1 ? "UPLOAD FILE" : step === 2 ? "CONFIGURE TEMPLATE" : step === 3 ? "REVIEW" : "DONE"}</p>
          </div>
          <button onClick={onClose} className="text-[#857973] hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-auto p-6">
          {step === 1 && (
            <div className="space-y-5">
              <div>
                <h3 className="text-white text-base font-medium mb-1">Upload an Excel or CSV file with prospects</h3>
                <p className="text-sm text-[#857973]">Required columns: <code className="text-[#F59073] font-mono text-xs">company_name</code>, <code className="text-[#F59073] font-mono text-xs">to_email</code>, <code className="text-[#F59073] font-mono text-xs">preview_url</code>. Optional: <code className="text-[#F59073] font-mono text-xs">to_name</code>, <code className="text-[#F59073] font-mono text-xs">phone</code> (E.164), <code className="text-[#F59073] font-mono text-xs">intro_text</code>, <code className="text-[#F59073] font-mono text-xs">org_number</code>, <code className="text-[#F59073] font-mono text-xs">country</code>.</p>
              </div>
              <button onClick={downloadTemplate} data-testid="bulk-download-template"
                className="inline-flex items-center gap-2 bg-[#1E3050] text-white px-3 py-2 rounded-sm text-sm hover:bg-[#1E3050]/70 border border-[#F59073]/30">
                <Download className="w-4 h-4" /> Download template
              </button>

              <label className="flex flex-col items-center justify-center gap-3 border-2 border-dashed border-[#1E3050] hover:border-[#F59073]/50 rounded-sm p-12 cursor-pointer bg-[#0A1628]/40" data-testid="bulk-upload-zone">
                {parsing ? (
                  <Loader2 className="w-8 h-8 text-[#F59073] animate-spin" />
                ) : (
                  <FileSpreadsheet className="w-12 h-12 text-[#857973]" />
                )}
                <span className="text-white text-sm">{parsing ? "Parsing…" : "Click to choose file or drop here"}</span>
                <span className="text-xs text-[#857973]">.xlsx or .csv</span>
                <input type="file" accept=".xlsx,.csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  className="hidden" data-testid="bulk-file-input"
                  onChange={e => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onFile(f); }}
                  onDrop={e => { e.preventDefault(); const f = e.dataTransfer?.files?.[0]; if (f) onFile(f); }} />
              </label>
              {parseError && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-sm p-3 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                  <span className="text-xs text-red-300">{parseError}</span>
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <div className="bg-[#0A1628] border border-[#F59073]/20 rounded-sm p-3 text-xs text-[#857973]">
                <span className="font-mono text-[10px] tracking-wider text-[#F59073]">PLACEHOLDERS:</span> <code>{"{company}"}</code> · <code>{"{name}"}</code> · <code>{"{first_name}"}</code> · <code>{"{preview_url}"}</code> — these get replaced per prospect.
              </div>

              <div>
                <label className="text-[10px] font-mono text-[#857973] tracking-[0.15em] block mb-1">EMAIL SUBJECT</label>
                <input value={config.subject_template} onChange={e => setConfig(p => ({ ...p, subject_template: e.target.value }))}
                  data-testid="bulk-subject"
                  className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#F59073] outline-none" />
              </div>

              <div>
                <label className="text-[10px] font-mono text-[#857973] tracking-[0.15em] block mb-1">EMAIL BODY</label>
                <textarea value={config.body_template} onChange={e => setConfig(p => ({ ...p, body_template: e.target.value }))}
                  rows={8} data-testid="bulk-body"
                  className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#F59073] outline-none font-mono leading-relaxed resize-y" />
              </div>

              <div>
                <label className="text-[10px] font-mono text-[#857973] tracking-[0.15em] block mb-1">SMS MESSAGE (sent only when phone is present)</label>
                <textarea value={config.sms_template} onChange={e => setConfig(p => ({ ...p, sms_template: e.target.value }))}
                  rows={2} data-testid="bulk-sms"
                  className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#F59073] outline-none font-mono leading-relaxed resize-y" />
                <p className="text-[10px] text-[#857973] mt-1">{config.sms_template.length} chars · References are NOT included in SMS.</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-mono text-[#857973] tracking-[0.15em] block mb-1">CALENDLY URL (booking page)</label>
                  <input value={config.calendly_url} onChange={e => setConfig(p => ({ ...p, calendly_url: e.target.value }))}
                    placeholder="https://calendly.com/..." data-testid="bulk-calendly"
                    className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#F59073] outline-none font-mono" />
                </div>
                <div>
                  <label className="text-[10px] font-mono text-[#857973] tracking-[0.15em] block mb-1">VIDEO URL (optional intro)</label>
                  <input value={config.video_url} onChange={e => setConfig(p => ({ ...p, video_url: e.target.value }))}
                    placeholder="https://..." data-testid="bulk-video"
                    className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#F59073] outline-none font-mono" />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-[10px] font-mono text-[#857973] tracking-[0.15em] block mb-1">SETUP PRICE</label>
                  <input type="number" value={config.price_setup} onChange={e => setConfig(p => ({ ...p, price_setup: Number(e.target.value) }))}
                    className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#F59073] outline-none" />
                </div>
                <div>
                  <label className="text-[10px] font-mono text-[#857973] tracking-[0.15em] block mb-1">MONTHLY PRICE</label>
                  <input type="number" value={config.price_monthly} onChange={e => setConfig(p => ({ ...p, price_monthly: Number(e.target.value) }))}
                    className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#F59073] outline-none" />
                </div>
                <div>
                  <label className="text-[10px] font-mono text-[#857973] tracking-[0.15em] block mb-1">CURRENCY</label>
                  <select value={config.currency} onChange={e => setConfig(p => ({ ...p, currency: e.target.value }))}
                    className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#F59073] outline-none">
                    {["SEK", "EUR", "USD", "NOK", "DKK"].map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              {refs.length > 0 && (
                <div>
                  <label className="text-[10px] font-mono text-[#857973] tracking-[0.15em] block mb-2">REFERENCES TO INCLUDE IN EMAIL ({selectedRefs.length} selected)</label>
                  <div className="grid grid-cols-2 gap-2 max-h-48 overflow-auto p-1">
                    {refs.map(r => (
                      <label key={r.id} className={`flex items-center gap-2 cursor-pointer rounded-sm border p-2 ${selectedRefs.includes(r.id) ? "border-[#F59073]/50 bg-[#F59073]/5" : "border-[#1E3050] bg-[#0A1628]"}`}>
                        <input type="checkbox" checked={selectedRefs.includes(r.id)}
                          onChange={() => setSelectedRefs(prev => prev.includes(r.id) ? prev.filter(x => x !== r.id) : [...prev, r.id])}
                          className="accent-[#F59073]" />
                        <div className="min-w-0 flex-1">
                          <p className="text-xs text-white truncate">{r.client}</p>
                          <p className="text-[10px] text-[#857973] truncate">{r.url}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-between pt-3 border-t border-[#1E3050]">
                <button onClick={() => setStep(1)} className="inline-flex items-center gap-1 text-[#857973] hover:text-white text-sm">
                  <ChevronLeft className="w-4 h-4" /> Back
                </button>
                <button onClick={() => setStep(3)} data-testid="bulk-next-review"
                  className="inline-flex items-center gap-1 bg-[#F59073] text-black px-4 py-2 rounded-sm text-sm font-medium hover:shadow-[0_0_15px_rgba(245,144,115,0.3)]">
                  Review prospects <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <div>
                  <span className="text-white font-medium">{validRows.length}</span> <span className="text-[#857973]">valid</span>
                  <span className="text-[#857973] mx-2">·</span>
                  <span className="text-red-400">{rows.length - validRows.length}</span> <span className="text-[#857973]">invalid</span>
                  <span className="text-[#857973] mx-2">·</span>
                  <span className="text-white">{rows.length}</span> <span className="text-[#857973]">total</span>
                </div>
              </div>
              <div className="bg-[#0A1628] border border-[#1E3050] rounded-sm overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-[#1E3050]/30 text-[#857973] font-mono">
                    <tr>
                      <th className="text-left px-3 py-2">#</th>
                      <th className="text-left px-3 py-2">Company</th>
                      <th className="text-left px-3 py-2">Name</th>
                      <th className="text-left px-3 py-2">Email</th>
                      <th className="text-left px-3 py-2">Phone</th>
                      <th className="text-left px-3 py-2">Preview URL</th>
                      <th className="text-left px-3 py-2">Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(r => (
                      <tr key={r.row_index} className={`border-t border-[#1E3050] ${r.valid ? "" : "bg-red-500/5"}`} data-testid={`bulk-row-${r.row_index}`}>
                        <td className="px-3 py-2 font-mono text-[#857973]">{r.row_index}</td>
                        <td className="px-3 py-2 text-white">{r.company_name || "—"}</td>
                        <td className="px-3 py-2 text-neutral-300">{r.to_name || "—"}</td>
                        <td className="px-3 py-2 text-neutral-300 font-mono">{r.to_email}</td>
                        <td className="px-3 py-2 text-neutral-300 font-mono">{r.phone || "—"}</td>
                        <td className="px-3 py-2 text-neutral-300 truncate max-w-[180px]" title={r.preview_url}>{r.preview_url || "—"}</td>
                        <td className="px-3 py-2">
                          {r.valid ? (
                            <span className="inline-flex items-center gap-1 text-green-400 text-[10px] font-mono"><Check className="w-3 h-3" /> READY</span>
                          ) : (
                            <span className="text-red-400 text-[10px]" title={r.errors.join(", ")}>{r.errors[0]}</span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <button onClick={() => removeRow(r.row_index)} className="text-[#857973] hover:text-red-400">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-between pt-3 border-t border-[#1E3050]">
                <button onClick={() => setStep(2)} className="inline-flex items-center gap-1 text-[#857973] hover:text-white text-sm">
                  <ChevronLeft className="w-4 h-4" /> Back
                </button>
                <button onClick={createDrafts} disabled={creating || validRows.length === 0} data-testid="bulk-create-drafts-btn"
                  className="inline-flex items-center gap-2 bg-[#F59073] text-black px-4 py-2 rounded-sm text-sm font-medium hover:shadow-[0_0_15px_rgba(245,144,115,0.3)] disabled:opacity-50">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Star className="w-4 h-4" />}
                  {creating ? "Creating drafts…" : `Create ${validRows.length} drafts`}
                </button>
              </div>
            </div>
          )}

          {step === 4 && result && (
            <div className="text-center py-8 space-y-4" data-testid="bulk-result">
              <div className="flex flex-col items-center gap-2">
                <div className="w-14 h-14 rounded-full bg-green-500/15 border border-green-500/40 flex items-center justify-center">
                  <Check className="w-7 h-7 text-green-400" />
                </div>
                <h3 className="text-xl text-white font-medium">{result.created} drafts created</h3>
                <p className="text-sm text-[#857973]">All prospects now appear in the Outreach list as drafts.<br />Click "Send" on each row when you're ready — references, video and Calendly are pre-filled.</p>
                {result.skipped?.length > 0 && (
                  <p className="text-xs text-yellow-400 mt-2">{result.skipped.length} rows were skipped due to missing data.</p>
                )}
              </div>
              <button onClick={onClose}
                className="inline-flex items-center gap-2 bg-[#F59073] text-black px-5 py-2 rounded-sm text-sm font-medium hover:shadow-[0_0_15px_rgba(245,144,115,0.3)]">
                <Send className="w-4 h-4" /> Go to Outreach list
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
